import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, ChevronRight, MessageSquarePlus, Loader2, Clock, CheckCircle, XCircle, AlertTriangle, Search, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getMyBugReports, getBugReport, addBugFollowUp, normalizeFileUrl } from '@/lib/api';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

const SEV_STYLES = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const STATUS_STYLES = {
  NEW: { icon: Clock, cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' },
  IN_REVIEW: { icon: Eye, cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30' },
  IN_PROGRESS: { icon: Loader2, cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' },
  RESOLVED: { icon: CheckCircle, cls: 'bg-green-100 text-green-700 dark:bg-green-900/30' },
  CLOSED: { icon: XCircle, cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800' },
  DUPLICATE: { icon: Bug, cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30' },
  WONT_FIX: { icon: XCircle, cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800' },
};

const CATEGORY_LABELS = {
  listing_issue: 'Listing issue', payment_issue: 'Payment issue', account_issue: 'Account issue',
  search_matching: 'Search & matching', invoice_billing: 'Invoice or billing', email_not_received: 'Email not received',
  performance_loading: 'Performance or loading', other: 'Other',
};

export default function MyBugReportsPage() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [followUpSending, setFollowUpSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await getMyBugReports(params);
      setReports(res.data.reports);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const openDetail = async (reportId) => {
    setDetailLoading(true);
    try {
      const res = await getBugReport(reportId);
      setDetail(res.data);
      setFollowUp('');
    } catch {
      toast.error('Failed to load report');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUp.trim() || !detail) return;
    setFollowUpSending(true);
    try {
      await addBugFollowUp(detail.report_id, { comment: followUp });
      toast.success('Follow-up sent');
      setDetail(d => ({ ...d, follow_up_comment: { comment: followUp, created_at: new Date().toISOString() } }));
      setFollowUp('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setFollowUpSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="my-bug-reports-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bug className="h-6 w-6 text-orange-500" />
            My Reports
          </h1>
          <Badge variant="outline">{total} total</Badge>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="my-reports-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.keys(STATUS_STYLES).map(s => (
                <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Report List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : reports.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No reports found.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {reports.map(r => {
              const st = STATUS_STYLES[r.status] || STATUS_STYLES.NEW;
              const StIcon = st.icon;
              return (
                <Card
                  key={r.report_id}
                  className="cursor-pointer hover:border-orange-300 transition-colors"
                  onClick={() => openDetail(r.report_id)}
                  data-testid={`my-report-${r.report_id}`}
                >
                  <CardContent className="py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-muted-foreground">{r.report_id}</span>
                        <Badge className={SEV_STYLES[r.severity]} variant="secondary">{r.severity}</Badge>
                        <Badge className={st.cls} variant="secondary">
                          <StIcon className="h-3 w-3 mr-1" />
                          {r.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="font-medium truncate">{r.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {CATEGORY_LABELS[r.category] || r.category} — {new Date(r.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto" data-testid="my-report-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-orange-500">{detail.report_id}</span>
                  <Badge className={SEV_STYLES[detail.severity]} variant="secondary">{detail.severity}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={(STATUS_STYLES[detail.status] || STATUS_STYLES.NEW).cls} variant="secondary">
                    {detail.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="font-medium">{CATEGORY_LABELS[detail.category] || detail.category}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Title</p>
                  <p className="font-medium">{detail.title}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-3 rounded">{detail.description}</p>
                </div>
                {detail.steps_to_reproduce && (
                  <div>
                    <p className="text-sm text-muted-foreground">Steps to Reproduce</p>
                    <p className="text-sm whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-3 rounded">{detail.steps_to_reproduce}</p>
                  </div>
                )}
                {detail.page_url && (
                  <div>
                    <p className="text-sm text-muted-foreground">Page URL</p>
                    <p className="text-sm break-all">{detail.page_url}</p>
                  </div>
                )}
                {detail.browser_info && (
                  <div>
                    <p className="text-sm text-muted-foreground">Browser & Device</p>
                    <p className="text-sm">{detail.browser_info}</p>
                  </div>
                )}
                {detail.attachments?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Attachments</p>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {detail.attachments.map((a, i) => (
                        <a key={i} href={normalizeFileUrl(a.url)} target="_blank" rel="noopener noreferrer" className="text-sm text-orange-500 underline">{a.filename}</a>
                      ))}
                    </div>
                  </div>
                )}
                {detail.duplicate_of && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                    <p className="text-sm">This report is a duplicate of <strong className="font-mono">{detail.duplicate_of}</strong></p>
                  </div>
                )}

                {/* Resolution notes */}
                {detail.resolution_notes?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-green-600 mb-1">Resolution Notes</p>
                    {detail.resolution_notes.map((n, i) => (
                      <div key={i} className="text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded mb-2">
                        <p>{n.note}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(n.added_at).toLocaleString('de-DE')}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Follow-up comment */}
                {detail.follow_up_comment ? (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <p className="text-xs text-muted-foreground mb-1">Your follow-up comment</p>
                    <p className="text-sm">{detail.follow_up_comment.comment}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(detail.follow_up_comment.created_at).toLocaleString('de-DE')}</p>
                  </div>
                ) : (
                  !['RESOLVED', 'CLOSED', 'WONT_FIX'].includes(detail.status) && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Add a follow-up comment (once)</p>
                      <Textarea
                        value={followUp}
                        onChange={e => setFollowUp(e.target.value.slice(0, 500))}
                        placeholder="Additional information..."
                        rows={2}
                        data-testid="follow-up-textarea"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-muted-foreground">{followUp.length}/500</span>
                        <Button
                          size="sm"
                          onClick={handleFollowUp}
                          disabled={!followUp.trim() || followUpSending}
                          className="bg-orange-500 hover:bg-orange-600"
                          data-testid="follow-up-submit-btn"
                        >
                          {followUpSending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <MessageSquarePlus className="h-3 w-3 mr-1" />}
                          Send Follow-up
                        </Button>
                      </div>
                    </div>
                  )
                )}

                {/* Status history */}
                {detail.status_history?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Status History</p>
                    <div className="space-y-2">
                      {detail.status_history.map((sh, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className="font-mono text-muted-foreground w-36 shrink-0">{new Date(sh.changed_at).toLocaleString('de-DE')}</span>
                          <Badge className={(STATUS_STYLES[sh.status] || STATUS_STYLES.NEW).cls} variant="secondary">{sh.status.replace('_', ' ')}</Badge>
                          {sh.note && <span className="text-muted-foreground">— {sh.note}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
